import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import {
  fetchInfoCapitalGoalsData, updateInformationCapitalGoal,
  fetchDataTypes,
  fetchStrategicGoals,
  fetchCustomerValues,
  fetchFinancialGoals,
  fetchStrategicIssues,
  fetchHumanCapitalGoals,
  fetchInfoCapitalGoalsApp,
  fetchInfoCapitalGoalsIt,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import { useAuth } from "../../providers/AuthProvider";

const TYPE = "InformationCapitalGoalData";

export default function SmInfoCapitalGoalDataEditPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const { companyId } = useAuth();
  const t = (id) => intl.formatMessage({ id });

  const [successFactorName, setSuccessFactorName] = useState("");
  const [successFactorDescription, setSuccessFactorDescription] = useState("");
  const [selectedDataType, setSelectedDataType] = useState(null);
  const [selectedNames, setSelectedNames] = useState([]);
  const [sourceIds, setSourceIds] = useState([]);
  const [dataTypes, setDataTypes] = useState([]);
  const [strategicGoals, setStrategicGoals] = useState([]);
  const [customerValues, setCustomerValues] = useState([]);
  const [financialGoals, setFinancialGoals] = useState([]);
  const [strategicIssues, setStrategicIssues] = useState([]);
  const [humanCapitalGoals, setHumanCapitalGoals] = useState([]);
  const [infoCapitalGoalsApp, setInfoCapitalGoalsApp] = useState([]);
  const [infoCapitalGoalsIt, setInfoCapitalGoalsIt] = useState([]);
  const [infoCapitalGoalsData, setInfoCapitalGoalsData] = useState([]);
  const [fullData, setFullData] = useState(null);

  // 指標は選択されたデータタイプの indicators から取得
  const sourceIndicators = selectedDataType?.indicators || [];

  useEffect(() => {
    (async () => {
      try {
        const [goals, dtypes, sgGoals, cvGoals, fgGoals, issues, hcGoals, icApp, icIt] = await Promise.all([
          fetchInfoCapitalGoalsData(businessDomainId),
          fetchDataTypes(businessDomainId),
          fetchStrategicGoals(businessDomainId).catch(() => []),
          fetchCustomerValues(businessDomainId).catch(() => []),
          fetchFinancialGoals(businessDomainId).catch(() => []),
          fetchStrategicIssues(businessDomainId).catch(() => []),
          fetchHumanCapitalGoals(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsApp(businessDomainId).catch(() => []),
          fetchInfoCapitalGoalsIt(businessDomainId).catch(() => []),
        ]);
        setDataTypes(Array.isArray(dtypes) ? dtypes : []);
        setStrategicGoals(Array.isArray(sgGoals) ? sgGoals : []);
        setCustomerValues(Array.isArray(cvGoals) ? cvGoals : []);
        setFinancialGoals(Array.isArray(fgGoals) ? fgGoals : []);
        setStrategicIssues(Array.isArray(issues) ? issues : []);
        setHumanCapitalGoals(Array.isArray(hcGoals) ? hcGoals : []);
        setInfoCapitalGoalsApp(Array.isArray(icApp) ? icApp : []);
        setInfoCapitalGoalsIt(Array.isArray(icIt) ? icIt : []);
        setInfoCapitalGoalsData((Array.isArray(goals) ? goals : []).filter((g) => String(g.successFactorId) !== String(successFactorId)));
        const found = (Array.isArray(goals) ? goals : []).find(
          (g) => String(g.successFactorId) === String(successFactorId)
        );
        if (found) {
          setFullData(found);
          setSuccessFactorName(found.successFactorName || "");
          setSuccessFactorDescription(found.successFactorDescription || "");
          setSourceIds((found.sources || []).map((s) => s.successFactorId));
          setSelectedNames((found.indicators || []).map((ind) => ind.indicatorName));
          if (found.dataTypeId) {
            const foundType = (Array.isArray(dtypes) ? dtypes : []).find(
              (dt) => String(dt.dataTypeId) === String(found.dataTypeId)
            );
            if (foundType) setSelectedDataType(foundType);
          }
        }
      } catch (e) {
        notify.apiError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  const handleDataTypeChange = (e) => {
    const type = dataTypes.find((dt) => dt.dataTypeId === e.target.value);
    setSelectedDataType(type || null);
    setSelectedNames([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sourceIndicators.length > 0 && selectedNames.length === 0) {
      notify.error(t("financialGoal.indicatorRequired"));
      return;
    }
    try {
      const indicators = selectedNames
        .map((name) => sourceIndicators.find((ind) => ind.indicatorName === name))
        .filter(Boolean);
      const allGoals = [...strategicGoals, ...customerValues, ...financialGoals, ...strategicIssues, ...humanCapitalGoals, ...infoCapitalGoalsApp, ...infoCapitalGoalsIt, ...infoCapitalGoalsData];
      const sources = sourceIds
        .map((id) => {
          const goal = allGoals.find((s) => s.successFactorId === id);
          if (!goal) return null;
          return { successFactorId: goal.successFactorId, successFactorName: goal.successFactorName };
        })
        .filter(Boolean);
      await updateInformationCapitalGoal({
        ...fullData,
        successFactorName,
        successFactorDescription,
        dataTypeId: selectedDataType?.dataTypeId || "",
        indicators,
        sources,
        businessDomainId,
        companyId,
        successFactorId,
        successFactorType: TYPE,
      });
      notify.success(t("message.update.success"));
      navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-data/${successFactorId}`);
    } catch (err) {
      notify.apiError(err);
    }
  };

  if (!fullData) return null;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t("infoCapitalGoalData.edit.title")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth required
              label={t("successFactor.name")}
              value={successFactorName}
              onChange={(e) => setSuccessFactorName(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth multiline rows={3}
              label={t("successFactor.description")}
              value={successFactorDescription}
              onChange={(e) => setSuccessFactorDescription(e.target.value)}
            />
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("dataType.label")}</InputLabel>
              <Select
                value={selectedDataType?.dataTypeId || ""}
                label={t("dataType.label")}
                onChange={handleDataTypeChange}
              >
                <MenuItem value=""><em>—</em></MenuItem>
                {dataTypes.map((dt) => (
                  <MenuItem key={dt.dataTypeId} value={dt.dataTypeId}>
                    {dt.dataTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("orgCapitalGoal.sources.label")}</InputLabel>
              <Select
                multiple
                value={sourceIds}
                onChange={(e) => setSourceIds(e.target.value)}
                input={<OutlinedInput label={t("orgCapitalGoal.sources.label")} />}
              >
                {financialGoals.length > 0 && [
                  <MenuItem key="__fg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("financialGoal.list.title")} ──
                  </MenuItem>,
                  ...financialGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {customerValues.length > 0 && [
                  <MenuItem key="__cv_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("customerValue.list.title")} ──
                  </MenuItem>,
                  ...customerValues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {strategicGoals.length > 0 && [
                  <MenuItem key="__sg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("strategicGoal.list.title")} ──
                  </MenuItem>,
                  ...strategicGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {strategicIssues.length > 0 && [
                  <MenuItem key="__si_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("strategicIssue.list.title")} ──
                  </MenuItem>,
                  ...strategicIssues.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {humanCapitalGoals.length > 0 && [
                  <MenuItem key="__hcg_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("humanCapitalGoal.list.title")} ──
                  </MenuItem>,
                  ...humanCapitalGoals.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalGoalsApp.length > 0 && [
                  <MenuItem key="__ica_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalApp.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsApp.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalGoalsIt.length > 0 && [
                  <MenuItem key="__icit_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalIt.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsIt.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
                {infoCapitalGoalsData.length > 0 && [
                  <MenuItem key="__icd_header" disabled sx={{ fontWeight: 700, opacity: 1, color: "text.primary" }}>
                    ── {t("infoCapitalGoalData.list.title")} ──
                  </MenuItem>,
                  ...infoCapitalGoalsData.map((item) => (
                    <MenuItem key={item.successFactorId} value={item.successFactorId}>
                      {item.successFactorName}
                    </MenuItem>
                  )),
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>{t("financialGoal.selectIndicatorHint")}</InputLabel>
              <Select
                multiple
                disabled={sourceIndicators.length === 0}
                value={selectedNames}
                onChange={(e) => setSelectedNames(e.target.value)}
                input={<OutlinedInput label={t("financialGoal.selectIndicatorHint")} />}
              >
                {sourceIndicators.map((ind) => (
                  <MenuItem key={ind.indicatorName} value={ind.indicatorName}>
                    {ind.indicatorName}（{ind.unit}）
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit" disabled={!successFactorName.trim()}>
            {t("common.save")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/info-capital-goals-data/${successFactorId}`)}
          >
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
